import React, { useState, useMemo } from 'react';
import { Plus, X, Building2, Tag, Search, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/Button/ButtonSize';
import Modal from '@/components/ui/Modal';
import {
    useGetAllowedCompaniesQuery,
    useGetAllowedCategoriesQuery
} from '../services/userPermissions.queries';
import {
    useAddAllowedCompanyMutation,
    useDeleteAllowedCompanyMutation,
    useAddAllowedCategoryMutation,
    useDeleteAllowedCategoryMutation
} from '../services/userPermissions.mutations';
import { useGetCompaniesQuery } from '@/components/features/companies/services/companies.queries';
import { useGetCategoriesQuery } from '@/components/features/categories/services/categories.queries';

interface UserPermissionsProps {
    userId: string;
}

export const UserPermissions: React.FC<UserPermissionsProps> = ({ userId }) => {
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [categorySearchTerm, setCategorySearchTerm] = useState('');

    // Queries
    const { data: allowedCompanies = [], isLoading: loadingAllowedCompanies } = useGetAllowedCompaniesQuery(userId);
    const { data: allowedCategories = [], isLoading: loadingAllowedCategories } = useGetAllowedCategoriesQuery(userId);
    const { data: allCompanies = [] } = useGetCompaniesQuery();
    const { data: allCategories = [] } = useGetCategoriesQuery();

    // Mutations
    const addCompanyMutation = useAddAllowedCompanyMutation(userId);
    const deleteCompanyMutation = useDeleteAllowedCompanyMutation(userId);
    const addCategoryMutation = useAddAllowedCategoryMutation(userId);
    const deleteCategoryMutation = useDeleteAllowedCategoryMutation(userId);

    // Get available companies (not already added)
    const availableCompanies = allCompanies.filter(
        company => !allowedCompanies.some(ac => ac.company.company_id === company.company_id)
    );

    // Get available categories (not already added)
    const availableCategories = allCategories.filter(
        category => !allowedCategories.some(ac => ac.category.category_id === category.category_id)
    );

    // Filtered companies based on search
    const filteredCompanies = useMemo(() => {
        return availableCompanies.filter(company =>
            company.company_name.toLowerCase().includes(companySearchTerm.toLowerCase()) ||
            company.email_address.toLowerCase().includes(companySearchTerm.toLowerCase())
        );
    }, [availableCompanies, companySearchTerm]);

    // Filtered categories based on search
    const filteredCategories = useMemo(() => {
        return availableCategories.filter(category =>
            category.category_name.toLowerCase().includes(categorySearchTerm.toLowerCase()) ||
            category.description?.toLowerCase().includes(categorySearchTerm.toLowerCase())
        );
    }, [availableCategories, categorySearchTerm]);

    const toggleCompanySelection = (companyId: string) => {
        setSelectedCompanyIds(prev =>
            prev.includes(companyId)
                ? prev.filter(id => id !== companyId)
                : [...prev, companyId]
        );
    };

    const toggleCategorySelection = (categoryId: string) => {
        setSelectedCategoryIds(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleAddCompanies = async () => {
        if (selectedCompanyIds.length === 0) return;
        
        for (const companyId of selectedCompanyIds) {
            await addCompanyMutation.mutateAsync({ company_id: companyId });
        }
        setCompanyModalOpen(false);
        setSelectedCompanyIds([]);
        setCompanySearchTerm('');
    };

    const handleDeleteCompany = async (allowedCompanyId: string) => {
        await deleteCompanyMutation.mutateAsync(allowedCompanyId);
    };

    const handleAddCategories = async () => {
        if (selectedCategoryIds.length === 0) return;
        
        for (const categoryId of selectedCategoryIds) {
            await addCategoryMutation.mutateAsync({ category_id: categoryId });
        }
        setCategoryModalOpen(false);
        setSelectedCategoryIds([]);
        setCategorySearchTerm('');
    };

    const handleDeleteCategory = async (allowedCategoryId: string) => {
        await deleteCategoryMutation.mutateAsync(allowedCategoryId);
    };

    if (loadingAllowedCompanies || loadingAllowedCategories) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="bg-white h-64 rounded-lg"/>
                <div className="bg-white h-64 rounded-lg"/>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Allowed Companies */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Building2 className="w-5 h-5 text-primary-500" />
                            <CardTitle>Επιτρεπόμενες Εταιρείες</CardTitle>
                        </div>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SM}
                            onClick={() => setCompanyModalOpen(true)}
                            disabled={availableCompanies.length === 0}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Προσθήκη Εταιρείας
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {allowedCompanies.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Δεν έχουν προστεθεί επιτρεπόμενες εταιρείες</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {allowedCompanies.map((allowedCompany) => (
                                <div
                                    key={allowedCompany.user_allowed_company_id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <Building2 className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="font-medium text-gray-900">
                                                {allowedCompany.company.company_name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {allowedCompany.company.email_address}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant={ButtonVariant.GHOST}
                                        size={ButtonSize.SM}
                                        onClick={() => handleDeleteCompany(allowedCompany.user_allowed_company_id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Allowed Categories */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Tag className="w-5 h-5 text-primary-500" />
                            <CardTitle>Επιτρεπόμενες Κατηγορίες</CardTitle>
                        </div>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SM}
                            onClick={() => setCategoryModalOpen(true)}
                            disabled={availableCategories.length === 0}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Προσθήκη Κατηγορίας
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {allowedCategories.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Δεν έχουν προστεθεί επιτρεπόμενες κατηγορίες</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {allowedCategories.map((allowedCategory) => (
                                <div
                                    key={allowedCategory.user_allowed_category_id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <Tag className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <p className="font-medium text-gray-900">
                                                {allowedCategory.category.category_name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {allowedCategory.category.description}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant={ButtonVariant.GHOST}
                                        size={ButtonSize.SM}
                                        onClick={() => handleDeleteCategory(allowedCategory.user_allowed_category_id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add Company Modal */}
            <Modal
                open={companyModalOpen}
                setOpen={setCompanyModalOpen}
                title="Προσθήκη Επιτρεπόμενων Εταιρειών"
                type="default"
                icon={<Building2 className="h-6 w-6 text-primary-600" />}
                primaryAction={{
                    text: `Προσθήκη (${selectedCompanyIds.length})`,
                    onClick: handleAddCompanies
                }}
                secondaryAction={{
                    text: 'Ακύρωση',
                    onClick: () => {
                        setCompanyModalOpen(false);
                        setSelectedCompanyIds([]);
                        setCompanySearchTerm('');
                    }
                }}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Αναζητήστε και επιλέξτε μία ή περισσότερες εταιρείες για να προσθέσετε στις επιτρεπόμενες εταιρείες του χρήστη.
                    </p>
                    
                    {/* Search Input and Select All */}
                    <div className="space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Αναζήτηση εταιρείας..."
                                value={companySearchTerm}
                                onChange={(e) => setCompanySearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        {filteredCompanies.length > 0 && (
                            <div className="flex gap-2">
                                <Button
                                    variant={ButtonVariant.OUTLINE}
                                    size={ButtonSize.SM}
                                    onClick={() => {
                                        const allIds = filteredCompanies.map(c => c.company_id);
                                        setSelectedCompanyIds(allIds);
                                    }}
                                    className="flex-1"
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Επιλογή Όλων ({filteredCompanies.length})
                                </Button>
                                <Button
                                    variant={ButtonVariant.OUTLINE}
                                    size={ButtonSize.SM}
                                    onClick={() => setSelectedCompanyIds([])}
                                    className="flex-1"
                                    disabled={selectedCompanyIds.length === 0}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Αποεπιλογή Όλων
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Multi-select list */}
                    <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                        {filteredCompanies.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                                Δεν βρέθηκαν εταιρείες
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {filteredCompanies.map((company) => (
                                    <label
                                        key={company.company_id}
                                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedCompanyIds.includes(company.company_id)}
                                            onChange={() => toggleCompanySelection(company.company_id)}
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                        />
                                        <div className="ml-3 flex-1">
                                            <div className="flex items-center">
                                                {selectedCompanyIds.includes(company.company_id) && (
                                                    <Check className="w-4 h-4 text-primary-600 mr-2" />
                                                )}
                                                <span className="text-sm font-medium text-gray-900">
                                                    {company.company_name}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {company.email_address}
                                            </p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedCompanyIds.length > 0 && (
                        <div className="text-sm text-gray-600">
                            Επιλεγμένες: <span className="font-medium">{selectedCompanyIds.length}</span> εταιρεί{selectedCompanyIds.length === 1 ? 'α' : 'ες'}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Add Category Modal */}
            <Modal
                open={categoryModalOpen}
                setOpen={setCategoryModalOpen}
                title="Προσθήκη Επιτρεπόμενων Κατηγοριών"
                type="default"
                icon={<Tag className="h-6 w-6 text-primary-600" />}
                primaryAction={{
                    text: `Προσθήκη (${selectedCategoryIds.length})`,
                    onClick: handleAddCategories
                }}
                secondaryAction={{
                    text: 'Ακύρωση',
                    onClick: () => {
                        setCategoryModalOpen(false);
                        setSelectedCategoryIds([]);
                        setCategorySearchTerm('');
                    }
                }}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Αναζητήστε και επιλέξτε μία ή περισσότερες κατηγορίες για να προσθέσετε στις επιτρεπόμενες κατηγορίες του χρήστη.
                    </p>
                    
                    {/* Search Input and Select All */}
                    <div className="space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Αναζήτηση κατηγορίας..."
                                value={categorySearchTerm}
                                onChange={(e) => setCategorySearchTerm(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        {filteredCategories.length > 0 && (
                            <div className="flex gap-2">
                                <Button
                                    variant={ButtonVariant.OUTLINE}
                                    size={ButtonSize.SM}
                                    onClick={() => {
                                        const allIds = filteredCategories.map(c => c.category_id);
                                        setSelectedCategoryIds(allIds);
                                    }}
                                    className="flex-1"
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Επιλογή Όλων ({filteredCategories.length})
                                </Button>
                                <Button
                                    variant={ButtonVariant.OUTLINE}
                                    size={ButtonSize.SM}
                                    onClick={() => setSelectedCategoryIds([])}
                                    className="flex-1"
                                    disabled={selectedCategoryIds.length === 0}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    Αποεπιλογή Όλων
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Multi-select list */}
                    <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                        {filteredCategories.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 text-sm">
                                Δεν βρέθηκαν κατηγορίες
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {filteredCategories.map((category) => (
                                    <label
                                        key={category.category_id}
                                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedCategoryIds.includes(category.category_id)}
                                            onChange={() => toggleCategorySelection(category.category_id)}
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                        />
                                        <div className="ml-3 flex-1">
                                            <div className="flex items-center">
                                                {selectedCategoryIds.includes(category.category_id) && (
                                                    <Check className="w-4 h-4 text-primary-600 mr-2" />
                                                )}
                                                <span className="text-sm font-medium text-gray-900">
                                                    {category.category_name}
                                                </span>
                                            </div>
                                            {category.description && (
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {category.description}
                                                </p>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {selectedCategoryIds.length > 0 && (
                        <div className="text-sm text-gray-600">
                            Επιλεγμένες: <span className="font-medium">{selectedCategoryIds.length}</span> κατηγορί{selectedCategoryIds.length === 1 ? 'α' : 'ες'}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};
